import React, { useState } from "react";
import {
    Modal,
    Tabs,
    Tab,
    TextField,
    Button,
    Box,
    IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { login, register } from "../services/auth";
import { setAuth } from "../redux/slices/authSlice";
import { notify } from "./Toast";
import ForgotPasswordPopup from "./ForgotPasswordPopup";
import { useNavigate } from "react-router-dom";

const AuthPopup = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const [authTab, setAuthTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        signupEmail: "",
        password: "",
        signupPassword: "",
        confirmPassword: "",
    });
    const [errors, setErrors] = useState({});
    const [showForgotPopup, setShowForgotPopup] = useState(false);
    const navigate = useNavigate();

    const validateEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);


    const inputFieldStyles = {
        backgroundColor: "#1e1e1e",
        borderRadius: "8px",
        border: "1px solid #333",
        color: "#fff",
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (loading) return;

        const newErrors = {};
        if (!formData.email) newErrors.email = "Email is required";
        else if (!validateEmail(formData.email)) newErrors.email = "Invalid email format";

        if (!formData.password) newErrors.password = "Password is required";

        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setLoading(true);

        try {
            const response = await login({
                email: formData.email,
                password: formData.password,
                role: "client",
            });
            if (response.success) {
                dispatch(setAuth({
                    token: response.access_token,
                    user: response.user,
                    role: response.user.role,
                    purchasedSubscription: response.user.subscription_ids,
                    chatEnabled: response.user.chat_enabled,
                    activeSubscription: response.user.has_active_subscription,
                    onboardingCompleted: response.user.onboarding_completed,
                    subscription_has_document: response.user.has_document || false,
                }));
                notify.success(response.message);
                onClose();
                setFormData({});
            }
        } catch (error) {
            notify.error(error.response.data.message);
        } finally {
            setTimeout(() => setLoading(false), 600);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        if (loading) return;

        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required";
        if (!formData.signupEmail) newErrors.signupEmail = "Email is required";
        else if (!validateEmail(formData.signupEmail)) newErrors.signupEmail = "Invalid email format";
        if (!formData.signupPassword) newErrors.signupPassword = "Password is required";
        if (!formData.confirmPassword) newErrors.confirmPassword = "Confirm password is required";
        else if (formData.signupPassword !== formData.confirmPassword)
            newErrors.confirmPassword = "Passwords do not match";

        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setLoading(true);

        try {
            const response = await register({
                name: formData.name,
                email: formData.signupEmail,
                password: formData.signupPassword,
                password_confirmation: formData.confirmPassword,
                role: "client",
            });
            if (response.success) {
                dispatch(setAuth({
                    token: response.access_token,
                    user: response.user,
                    role: response.user.role,
                    activeSubscription: response.user.has_active_subscription,
                    subscription_has_document: response.user.has_document || false,
                }));
                notify.success(response.message);
                navigate("/");
                onClose();
                setFormData({});
            }
        } catch (error) {
            notify.error(error.response.data.message);
        } finally {
            setTimeout(() => setLoading(false), 600);
        }
    };

    return (
        <>
            <Modal  className="auth-modal" open={open} onClose={onClose} disableScrollLock
                BackdropProps={{
                    style: {
                        backgroundColor: "rgba(0, 0, 0, 0.8)", // darker than default (which is 0.5)
                    },
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 400 },
                        bgcolor: "#121212",
                        boxShadow: 24,
                        borderRadius: 2,
                        p: 4,
                        color: "#fff",
                    }}
                >
                    <Tabs
                        value={authTab}
                        onChange={(e, newValue) => setAuthTab(newValue)}
                        centered
                        textColor="inherit"
                        TabIndicatorProps={{
                            sx: { backgroundColor: "#00ffff", height: "3px" },
                        }}
                        sx={{ mb: 3 }}
                    >
                        <Tab label="Login" sx={{ color: "#fff" }} />
                        <Tab label="Sign Up" sx={{ color: "#fff" }} />
                    </Tabs>

                    {authTab === 0 ? (
                        <form onSubmit={handleLogin}>
                            <TextField
                                fullWidth
                                label="Email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                error={formData.email && !validateEmail(formData.email) || !!errors.email}
                                helperText={
                                    formData.email && !validateEmail(formData.email)
                                        ? "Invalid email format"
                                        : ""
                                        ||
                                        errors.email
                                }

                                variant="outlined"
                                margin="normal"
                                InputLabelProps={{ style: { color: "#aaa" } }}
                                InputProps={{ style: inputFieldStyles }}
                            />
                            <TextField
                                fullWidth
                                label="Password"
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                error={!!errors.password}
                                helperText={errors.password}
                                variant="outlined"
                                margin="normal"
                                InputLabelProps={{ style: { color: "#aaa" } }}
                                InputProps={{
                                    style: inputFieldStyles,
                                    endAdornment: (
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: "#aaa" }}>
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    ),
                                }}
                            />
                            <Box textAlign="right" mt={1}>
                                <Button
                                    onClick={() => {
                                        setShowForgotPopup(true);
                                        onClose(); // Optional: hide login popup to show forgot popup
                                    }}
                                    sx={{ color: "#00ffff", textTransform: "none", fontSize: "14px" }}
                                >
                                    Forgot Password?
                                </Button>
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                type="submit"
                                sx={{
                                    mt: 2, backgroundColor: "#00ffff", color: "#000",
                                    '&.Mui-disabled': {
                                        backgroundColor: "#00ffff",  // Keep background even when disabled
                                        color: "#000",
                                        mt: 2
                                    },
                                }}
                                disabled={loading}
                            >
                                {loading ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" /> : "Login"}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={handleSignup}>
                            <TextField
                                fullWidth label="Name" value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                error={!!errors.name}
                                helperText={errors.name}
                                variant="outlined" margin="normal"
                                InputLabelProps={{ style: { color: "#aaa" } }}
                                InputProps={{ style: inputFieldStyles }}
                            />
                            <TextField
                                fullWidth label="Email" value={formData.signupEmail}
                                onChange={(e) => setFormData({ ...formData, signupEmail: e.target.value })}
                                error={formData.signupEmail && !validateEmail(formData.signupEmail) || !!errors.signupEmail}
                                helperText={
                                    formData.signupEmail && !validateEmail(formData.signupEmail)
                                        ? "Invalid email format"
                                        : ""
                                        ||
                                        errors.signupEmail
                                }

                                variant="outlined" margin="normal"
                                InputLabelProps={{ style: { color: "#aaa" } }}
                                InputProps={{ style: inputFieldStyles }}
                            />
                            <TextField
                                fullWidth label="Password" type={showPassword ? "text" : "password"}
                                value={formData.signupPassword}
                                onChange={(e) => setFormData({ ...formData, signupPassword: e.target.value })}
                                error={!!errors.signupPassword}
                                helperText={errors.signupPassword}
                                variant="outlined" margin="normal"
                                InputLabelProps={{ style: { color: "#aaa" } }}
                                InputProps={{
                                    style: inputFieldStyles,
                                    endAdornment: (
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: "#aaa" }}>
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth label="Confirm Password" type={showConfirmPassword ? "text" : "password"}
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword}
                                variant="outlined" margin="normal"
                                InputLabelProps={{ style: { color: "#aaa" } }}
                                InputProps={{
                                    style: inputFieldStyles,
                                    endAdornment: (
                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: "#aaa" }}>
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    ),
                                }}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                type="submit"
                                sx={{
                                    mt: 2, backgroundColor: "#00ffff", color: "#000",
                                    '&.Mui-disabled': {
                                        backgroundColor: "#00ffff",  // Keep background even when disabled
                                        color: "#000",               // Optional: also keep text color
                                    },
                                }}
                                disabled={loading}
                            >
                                {loading ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" /> : "Sign Up"}
                            </Button>
                        </form>
                    )}
                </Box>
            </Modal>
            <ForgotPasswordPopup open={showForgotPopup} onClose={() => setShowForgotPopup(false)} />
        </>
    );
};

export default AuthPopup;
